import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add token to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Handle response errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            // Unauthorized - clear token and redirect to login
            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    requestOTP: (email) => api.post('/auth/request-otp', { email }),
    verifyOTP: (email, otp) => api.post('/auth/verify-otp', { email, otp }),
    loginWithPassword: (email, password) => api.post('/auth/login', { email, password }),
    getCurrentUser: () => api.get('/auth/me')
};

// Scanner API (Customer endpoints)
export const scannerAPI = {
    getScanners: () => api.get('/scanners'),
    getScanner: (id) => api.get(`/scanners/${id}`),
    updateScannerConfig: (id, config) => api.put(`/scanners/${id}/config`, config),
    sendTestWebhook: (id, testType) => api.post(`/scanners/${id}/test-webhook`, { testType }),
    getRecentScanLogs: () => api.get('/scanners/scan-logs/recent')
};

// Admin API
export const adminAPI = {
    // User management
    getUsers: (params) => api.get('/admin/users', { params }),
    getUser: (id) => api.get(`/admin/users/${id}`),
    createUser: (data) => api.post('/admin/users', data),
    updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
    deleteUser: (id) => api.delete(`/admin/users/${id}`),

    // Scanner management
    getAllScanners: () => api.get('/admin/scanners'),
    createScanner: (data) => api.post('/admin/scanners', data),
    updateScanner: (id, data) => api.put(`/admin/scanners/${id}`, data),
    deleteScanner: (id) => api.delete(`/admin/scanners/${id}`),

    // Scanner-User associations
    getScannerUsers: (scannerId) => api.get(`/admin/scanners/${scannerId}/users`),
    setScannerUsers: (scannerId, userIds) => api.put(`/admin/scanners/${scannerId}/users`, { userIds: userIds })
};

// Webhook API
export const webhookAPI = {
    getWebhooks: () => api.get('/webhooks'),
    getWebhook: (id) => api.get(`/webhooks/${id}`),
    createWebhook: (data) => api.post('/webhooks', data),
    updateWebhook: (id, data) => api.put(`/webhooks/${id}`, data),
    deleteWebhook: (id) => api.delete(`/webhooks/${id}`),
    getRecentExecutionLogs: () => api.get('/webhooks/execution-logs/recent'),
    getWebhookExecutionLogs: (webhookId, params) => api.get(`/webhooks/${webhookId}/execution-logs`, { params })
};

// Security Policy API
export const securityPolicyAPI = {
    getPolicies: () => api.get('/security-policies'),
    getPolicy: (id) => api.get(`/security-policies/${id}`),
    createPolicy: (data) => api.post('/security-policies', data),
    updatePolicy: (id, data) => api.put(`/security-policies/${id}`, data),
    deletePolicy: (id) => api.delete(`/security-policies/${id}`)
};

// Audit Log API
export const auditLogAPI = {
    getAuditLogs: (params) => api.get('/audit-logs', { params }),
    getResourceAuditLogs: (resourceType, resourceId) => api.get(`/audit-logs/${resourceType}/${resourceId}`)
};

export default api;
